// Continuous RTC-stats ring buffer (MV-1077).
//
// The on-demand Report It capture samples getStats() only a couple of times at
// report time — which misses an intermittent freeze unless the user happens to
// file the report during the freeze. This module-scoped store samples
// inbound-rtp stats on a fixed cadence for the *whole* session into a capped
// ring buffer, so whenever a report is filed the recent freeze / jitter / loss
// history is already inside the captured window.
//
// In-memory by design (mirrors connectionStateStore). localStorage would be the
// wrong tool: it persists across sessions (a fresh report shipping a previous
// stream's samples is a correctness hazard), its writes are synchronous and
// would add main-thread jitter to the very pipeline we're measuring, and it
// re-serialises the whole array per tick against a 5 MB quota — all to survive
// a reload that a video freeze never triggers.

import { RtcStatsSample } from './snapshotTypes';
import {
  extractRtcVideoSample,
  extractRtcAudioSample,
  RtcSampleResult,
} from './captureSnapshot';

// 2s cadence matches the existing GameCast stats interval; 90 samples keeps the
// last ~3 min — enough to contain a just-noticed freeze while keeping the
// size-capped POST /reports body small.
const DEFAULT_INTERVAL_MS = 2000;
const MAX_SAMPLES = 90;

type IntervalHandle = ReturnType<typeof setInterval>;

let videoSamples: RtcStatsSample[] = [];
let audioSamples: RtcStatsSample[] = [];
let timer: IntervalHandle | null = null;
let startMs = 0;
let cap = MAX_SAMPLES;

function pushCapped(buffer: RtcStatsSample[], sample: RtcStatsSample): void {
  buffer.push(sample);

  if (buffer.length > cap) {
    buffer.shift();
  }
}

export interface StartRtcStatsSamplingOptions {
  getStats: () => Promise<RTCStatsReport>;
  intervalMs?: number;
  maxSamples?: number;
  now?: () => number;
  /** Injectable timer for deterministic tests (defaults to setInterval). */
  setIntervalFn?: (cb: () => void, ms: number) => IntervalHandle;
}

/**
 * Begin sampling inbound-rtp stats into the ring buffer. Idempotent: a GameCast
 * reconnect re-enters the `connected` state, but we want one continuous series
 * across the reconnect rather than a restarted (cleared) buffer, so a second
 * call while already running is a no-op.
 */
export function startRtcStatsSampling(
  opts: StartRtcStatsSamplingOptions,
): void {
  if (timer !== null) {
    return;
  }

  const now = opts.now ?? ((): number => Date.now());
  const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
  const setIntervalFn =
    opts.setIntervalFn ??
    ((cb: () => void, ms: number): IntervalHandle => setInterval(cb, ms));

  cap = Math.max(1, opts.maxSamples ?? MAX_SAMPLES);
  startMs = now();

  const tick = async (): Promise<void> => {
    let report: RTCStatsReport;

    try {
      report = await opts.getStats();
    } catch {
      // A failed getStats just skips this tick — the next one resumes the
      // series, exactly as the on-demand window tolerates a short series.
      return;
    }

    const atMs = now() - startMs;
    const videoSample = extractRtcVideoSample(report, atMs);
    const audioSample = extractRtcAudioSample(report, atMs);

    if (videoSample) {
      pushCapped(videoSamples, videoSample);
    }

    if (audioSample) {
      pushCapped(audioSamples, audioSample);
    }
  };

  // setInterval ignores the returned promise; tests capture `tick` via the
  // injected timer and await it directly.
  timer = setIntervalFn(tick, intervalMs);
}

/** Stop sampling. Safe to call when not running. The buffer is retained so a
 *  report filed after the stream ends still carries the recent window. */
export function stopRtcStatsSampling(
  clearIntervalFn: (handle: IntervalHandle) => void = (handle): void =>
    clearInterval(handle),
): void {
  if (timer !== null) {
    clearIntervalFn(timer);
    timer = null;
  }
}

/**
 * The rolling video + audio sample window for the Report It snapshot. Returns
 * undefined until at least one sample has been buffered (e.g. a report filed
 * before any stream started), so the capture can fall back to its on-demand
 * window.
 */
export function getBufferedRtcSamples(): RtcSampleResult | undefined {
  if (videoSamples.length === 0 && audioSamples.length === 0) {
    return undefined;
  }

  return {
    video: [...videoSamples],
    audio: [...audioSamples],
  };
}

/** Test-only reset — the store is module-scoped for the page lifetime. */
export function resetRtcStatsBufferForTests(): void {
  if (timer !== null) {
    clearInterval(timer);
  }

  videoSamples = [];
  audioSamples = [];
  timer = null;
  startMs = 0;
  cap = MAX_SAMPLES;
}
